import React, { FC, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import { Aside } from '../components/core/Aside'
import { BaseComponent } from '../components/core/BaseComponent'
import { EditConfirmationPage } from './EditConfirmationPage'
import { EditExperienceInfoPage } from './EditExperienceInfoPage'
import { EditExpirationPage } from './EditExpirationPage'
import { EditLinksPage } from './EditLinksPage'
import { EditProductsPage } from './EditProductsPage'
import { PageComponent } from '../components/core/PageComponent'
import { WizardStepperHeader } from '../components/WizardStepperHeader'
import { VisualizationPage } from './VisualizationModePage'
import { useSelector } from 'react-redux'
import { ExperienceType } from '../types/vmdata'
import { Experience } from '../types/expType'
import EditScreensaverPage from './EditScreensaverPage'

export const getWizardSteps = (
  expType: ExperienceType
): {
  step: string
  component: React.ReactNode
}[] => {
  if (expType === Experience.DigitalWindow)
    return [
      { step: 'experience info', component: <EditExperienceInfoPage /> },
      { step: 'expiration', component: <EditExpirationPage /> },
      { step: 'products', component: <EditProductsPage /> },
      { step: 'screensaver', component: <EditScreensaverPage /> },
      { step: 'deploy', component: <EditConfirmationPage /> },
    ]
  else {
    return [
      { step: 'experience info', component: <EditExperienceInfoPage /> },
      { step: 'expiration', component: <EditExpirationPage /> },
      { step: 'links', component: <EditLinksPage /> },
      { step: 'visualization', component: <VisualizationPage /> },
      { step: 'products', component: <EditProductsPage /> },
      { step: 'deploy', component: <EditConfirmationPage /> },
    ]
  }
}

interface PropsWizardStep {
  step: string
  isNewProduct?: boolean
}

const WizardStep: FC<PropsWizardStep> = ({ step, isNewProduct }) => {
  switch (step) {
    case 'experience info':
      return <EditExperienceInfoPage />
    case 'expiration':
      return <EditExpirationPage />
    case 'visualization':
      return <VisualizationPage />
    case 'products':
      return <EditProductsPage isNewProduct={isNewProduct} />
    case 'links':
      return <EditLinksPage />
    case 'deploy':
      return <EditConfirmationPage />
    case 'screensaver':
      return <EditScreensaverPage isNewProduct={isNewProduct} />
    default:
      return <EditExperienceInfoPage />
  }
}

interface Props {
  isNewProduct?: boolean
}

export const StepContent: FC<Props> = ({ isNewProduct }) => {
  const { step } = useParams<{ step: string }>()

  return (
    <>
      <WizardStepperHeader />
      <WizardStep step={step ?? ''} isNewProduct={isNewProduct} />
    </>
  )
}

export const EditPage: React.FC = () => {
  const { pathname } = useLocation()
  const { step } = useParams<{ step: string }>()
  const path = pathname.substring(
    0,
    step ? pathname.indexOf(encodeURI(step)) - 1 : pathname.length
  )

  const navigate = useRef(useNavigate())
  const expType = useSelector((s) => s.vmData.expType)
  const steps = useRef(getWizardSteps(expType as ExperienceType))

  useEffect(() => {
    const targetStep = steps.current[0].step
    navigate.current(`${path}/${targetStep}`)
  }, [path])

  return (
    <BaseComponent>
      <Aside userMustConfirmBack={true} />
      <PageComponent>
        <Outlet />
      </PageComponent>
    </BaseComponent>
  )
}
